// src/pages/Home/components/TestimonialsSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaQuoteLeft,
  FaStar,
} from "react-icons/fa";

const testimonials = [
  {
    id: 1,
    name: "Sophie N.",
    role: "Dirigeante de club",
    text: "MonAppliClub nous a permis de centraliser beaucoup de tâches que nous faisions encore à la main. Les inscriptions, les documents et la communication sont bien plus simples à gérer au quotidien.",
  },
  {
    id: 2,
    name: "Julien N.",
    role: "Enseignant",
    text: "L’outil fait gagner un temps précieux sur le suivi des adhérents et des présences. On retrouve rapidement les informations utiles et l’organisation du club est beaucoup plus fluide.",
  },
  {
    id: 3,
    name: "Camille N.",
    role: "Dirigeante de club",
    text: "Nous avions besoin d’une solution claire et moderne pour mieux structurer le fonctionnement du club. MonAppliClub apporte une vraie amélioration sur la gestion et l’image du club.",
  },
  {
    id: 4,
    name: "David N.",
    role: "Enseignant",
    text: "J’apprécie surtout la simplicité d’utilisation. Les adhérents, les événements, les documents et les échanges sont mieux organisés, ce qui nous permet de nous concentrer davantage sur le sportif.",
  },
  {
    id: 5,
    name: "Laura N.",
    role: "Dirigeante de club",
    text: "MonAppliClub répond à des besoins très concrets de la vie d’un club. C’est une solution pratique, agréable à utiliser et pensée pour faciliter le quotidien des bénévoles et des responsables.",
  },
];

const cx = (...classes) => classes.filter(Boolean).join(" ");

function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = testimonials.length;

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, total]);

  const goTo = (index) => setCurrent(index);
  const goPrev = () => setCurrent((prev) => (prev - 1 + total) % total);
  const goNext = () => setCurrent((prev) => (prev + 1) % total);

  const active = testimonials[current];

  const previewLeft = useMemo(
    () => testimonials[(current - 1 + total) % total],
    [current, total]
  );
  const previewRight = useMemo(
    () => testimonials[(current + 1) % total],
    [current, total]
  );

  return (
    <section
      id="testimonials"
      className="bg-white py-16 sm:py-20 dark:bg-slate-950"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
            Témoignages
          </div>

          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
            Une solution appréciée pour sa simplicité,
            <span className="block text-sky-600 dark:text-sky-400">
              son efficacité et son utilité au quotidien
            </span>
          </h2>

          <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
            Quelques retours de nos utilisateurs, qui illustrent les bénéfices concrets de MonAppliClub dans la gestion de leurs clubs sportifs.
          </p>
        </div>

        <div
          className="mt-12"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* desktop layout */}
          <div className="hidden lg:grid lg:grid-cols-[1fr_minmax(0,760px)_1fr] lg:items-center lg:gap-6">
            <div className="opacity-45">
              <MiniPreviewCard item={previewLeft} />
            </div>

            <MainTestimonialCard
              item={active}
              onPrev={goPrev}
              onNext={goNext}
            />

            <div className="opacity-45">
              <MiniPreviewCard item={previewRight} />
            </div>
          </div>

          {/* mobile / tablet */}
          <div className="lg:hidden">
            <MainTestimonialCard
              item={active}
              onPrev={goPrev}
              onNext={goNext}
            />
          </div>

          {/* dots */}
          <div className="mt-8 flex items-center justify-center gap-3">
            {testimonials.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`Afficher le témoignage ${index + 1}`}
                className={cx(
                  "h-3 rounded-full transition-all duration-300",
                  current === index
                    ? "w-10 bg-sky-500"
                    : "w-3 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600"
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MainTestimonialCard({ item, onPrev, onNext }) {
  return (
    <div
      className={cx(
        "relative overflow-hidden rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm",
        "transition-all duration-500",
        "dark:border-white/10 dark:bg-slate-900 sm:p-8"
      )}
    >
      {/* décor */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-200/30 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-cyan-200/30 blur-3xl dark:bg-cyan-500/10" />
      </div>

      {/* controls */}
      <div className="absolute right-5 top-5 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          aria-label="Témoignage précédent"
        >
          <FaChevronLeft />
        </button>

        <button
          type="button"
          onClick={onNext}
          className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          aria-label="Témoignage suivant"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="relative">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
          <FaQuoteLeft className="text-xl" />
        </div>

        <div className="mt-5 flex items-center gap-1 text-amber-400">
          {Array.from({ length: 5 }).map((_, i) => (
            <FaStar key={i} />
          ))}
        </div>

        <blockquote className="mt-6 text-lg leading-9 text-slate-700 dark:text-slate-200 sm:text-2xl sm:leading-10">
          “{item.text}”
        </blockquote>

        <div className="mt-8 h-px bg-slate-200 dark:bg-white/10" />

        <div className="mt-6 flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 text-lg font-bold text-white shadow-lg">
            {item.name.charAt(0)}
          </div>

          <div>
            <div className="text-base font-bold text-slate-900 dark:text-white">
              {item.name}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {item.role}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniPreviewCard({ item }) {
  return (
    <div
      className={cx(
        "rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm",
        "dark:border-white/10 dark:bg-slate-900"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 text-sm font-bold text-white">
          {item.name.charAt(0)}
        </div>

        <div>
          <div className="font-semibold text-slate-900 dark:text-white">
            {item.name}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {item.role}
          </div>
        </div>
      </div>

      <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
        {item.text}
      </p>
    </div>
  );
}

export default TestimonialsSection;